import { DIALOG_DATA, DialogModule, DialogRef } from '@angular/cdk/dialog';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, signal } from '@angular/core';
import { SvgIconComponent } from '@ngneat/svg-icon';
import { TranslateModule } from '@ngx-translate/core';
import { ScreenSizeService } from '@libs/shared-services';
import { Arch, Quadrant, SwitchComponent } from '@libs/ui-lib';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { distinctUntilChanged, fromEvent, map, merge, startWith, tap } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { TeethComponent } from '../teeth';
import { TeethSelectorDialogData, TeethSelectorMode, TeethSelectorResult } from './teeth-selector-dialog.vm';
import { TeethGridComponent } from './components';

@Component({
  selector: 'lib-teeth-selector-dialog',
  templateUrl: './teeth-selector-dialog.component.html',
  styleUrl: './teeth-selector-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SvgIconComponent, TranslateModule, TeethComponent, DialogModule, SwitchComponent, ReactiveFormsModule, TeethGridComponent],
  providers: [ScreenSizeService],
})
export class TeethSelectorDialogComponent {
  protected readonly data: TeethSelectorDialogData = inject(DIALOG_DATA);
  protected readonly destroyRef = inject(DestroyRef);
  protected readonly TeethSelectorMode = TeethSelectorMode;

  protected readonly selectedArches = signal<Arch[]>([]);
  protected readonly selectedQuadrants = signal<Quadrant[]>([]);
  protected readonly rangeStartTooth = signal<number | null>(null);
  protected readonly rangeEndTooth = signal<number | null>(null);
  protected readonly selectedTeeth = signal<number[]>([]);
  protected readonly highlightedTeeth = signal<number[] | null>(null);
  protected readonly editing = signal<boolean>(false);
  protected readonly isShiftHeld = toSignal(
    merge(fromEvent<KeyboardEvent>(window, 'keyup'), fromEvent<KeyboardEvent>(window, 'keydown')).pipe(
      startWith(new KeyboardEvent('keydown')),
      map((event) => {
        return event.shiftKey;
      }),
      distinctUntilChanged(),
      tap((isShiftHeld) => {
        if (!isShiftHeld && this.data.mode !== TeethSelectorMode.Range) {
          this.highlightedTeeth.set(null);
        }
      }),
    ),
  );

  protected isConfirmDisabled = computed(() => {
    switch (this.data.mode) {
      case TeethSelectorMode.SingleTooth:
      case TeethSelectorMode.Full: {
        return this.selectedTeeth().length === 0;
      }
      case TeethSelectorMode.Arch: {
        return this.selectedArches().length === 0;
      }
      case TeethSelectorMode.Quadrant: {
        return this.selectedQuadrants().length === 0;
      }
      case TeethSelectorMode.Range: {
        return this.selectedTeeth().length < 2;
      }
      default: {
        return this.selectedTeeth().length === 0;
      }
    }
  });

  protected dialogTitleTranslationKey = computed(() => {
    switch (this.data.mode) {
      case TeethSelectorMode.SingleTooth:
      case TeethSelectorMode.Full: {
        return 'common_teeth_selector_title';
      }
      case TeethSelectorMode.Arch: {
        return 'common_teeth_selector_arch_title';
      }
      case TeethSelectorMode.Quadrant: {
        return 'common_teeth_selector_quadrant_title';
      }
      case TeethSelectorMode.Range: {
        return 'common_teeth_selector_range_title';
      }
      default: {
        return 'common_teeth_selector_title';
      }
    }
  });

  private readonly dialogRef = inject<DialogRef<TeethSelectorResult>>(DialogRef);

  protected readonly treatmentSwitchControl = new FormControl(false, { nonNullable: true });

  constructor() {
    if (this.data.selectedTeeth) {
      this.selectedTeeth.set([...this.data.selectedTeeth]);
      if (this.data.mode === TeethSelectorMode.Range) {
        this.highlightedTeeth.set([...this.data.selectedTeeth]);
      }
    }
    if (this.data.selectedArches) {
      this.selectedArches.set([...this.data.selectedArches]);
    }
    if (this.data.selectedQuadrants) {
      this.selectedQuadrants.set([...this.data.selectedQuadrants]);
    }
  }

  protected onCloseClick(): void {
    this.dialogRef.close();
  }

  protected onToothClick(tooth: number): void {
    if (this.data.mode === TeethSelectorMode.Range) {
      this.setHighlightedRange(tooth);
    } else if (this.isShiftHeld() && this.data.mode !== TeethSelectorMode.SingleTooth) {
      this.selectedTeeth.update((teeth) => {
        if (teeth) {
          return [...new Set([...teeth, ...this.highlightedTeeth()!])];
        }
        return this.highlightedTeeth()!;
      });
      this.highlightedTeeth.set([]);
      this.rangeStartTooth.set(null);
      this.rangeEndTooth.set(null);
    } else {
      this.updateSelection(tooth);
      this.highlightedTeeth.set(null);
      this.rangeStartTooth.set(tooth);
    }
  }

  protected onToothHovered(tooth: number): void {
    if (this.data.mode === TeethSelectorMode.SingleTooth) {
      return;
    }

    if (
      this.data.mode !== TeethSelectorMode.Range &&
      this.isShiftHeld() &&
      this.rangeStartTooth() !== null &&
      this.rangeEndTooth() !== tooth
    ) {
      this.highlightTeethInRange(tooth);
    }
  }

  protected highlightTeethInRange(tooth: number): void {
    this.rangeEndTooth.set(tooth);
    const start = this.rangeStartTooth()!;
    const end = this.rangeEndTooth()!;
    const teethInRange = this.data.teeth.map((t) => t.position).filter((position) => position >= start && position <= end);

    this.highlightedTeeth.set(teethInRange);
  }

  protected onArchClick(arch: Arch): void {
    const currentArches = this.selectedArches();

    if (this.data.editing) {
      this.selectedArches.set([arch]);
      return;
    }

    if (currentArches.includes(arch)) {
      this.selectedArches.set(currentArches.filter((a) => a !== arch));
    } else {
      this.selectedArches.set([...currentArches, arch]);
    }
  }

  protected onQuadrantClick(quadrant: Quadrant): void {
    const currentQuadrants = this.selectedQuadrants();

    if (this.data.editing) {
      this.selectedQuadrants.set([quadrant]);
      return;
    }

    if (currentQuadrants.includes(quadrant)) {
      this.selectedQuadrants.set(currentQuadrants.filter((q) => q !== quadrant));
    } else {
      this.selectedQuadrants.set([...currentQuadrants, quadrant]);
    }
  }

  protected onConfirmClick(): void {
    this.dialogRef.close({
      selectedTeeth: this.selectedTeeth(),
      selectedArches: this.selectedArches(),
      selectedQuadrants: this.selectedQuadrants(),
    });
  }

  private updateSelection(tooth: number): void {
    if (this.data.mode === TeethSelectorMode.SingleTooth) {
      this.selectedTeeth.set([tooth]);
      return;
    }
    this.selectedTeeth.update((teeth) => {
      if (teeth.includes(tooth)) {
        return teeth.filter((t) => t !== tooth);
      }
      return [...teeth, tooth];
    });
  }

  private setHighlightedRange(tooth: number): void {
    if (this.rangeStartTooth() === null || this.rangeStartTooth() === tooth || this.rangeStartTooth()! > tooth) {
      this.rangeStartTooth.set(tooth);
      this.rangeEndTooth.set(null);
      this.highlightedTeeth.set([]);
      this.selectedTeeth.set([tooth]);
    } else if (this.rangeStartTooth() !== null && this.rangeEndTooth() === null) {
      this.highlightTeethInRange(tooth);
      this.selectedTeeth.set(this.highlightedTeeth()!);
    } else if (this.rangeStartTooth() !== null && this.rangeEndTooth() !== null && this.highlightedTeeth() !== null) {
      this.rangeStartTooth.set(tooth);
      this.rangeEndTooth.set(null);
      this.highlightedTeeth.set(null);
      this.selectedTeeth.set([tooth]);
    }
  }
}
